import React from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
export function HeroKpi({ label, value, sublabel, delta, deltaLabel, spark, action, className, }) {
    const trend = typeof delta === 'number' ? formatTrend(delta) : null;
    const sparkData = (spark ?? []).filter((p) => Number.isFinite(p.value));
    const sparkColor = sparkColorForTone(trend?.tone);
    return (<Card className={cn('relative overflow-hidden border-border/60 bg-card/40 backdrop-blur-sm', className)}>
            <CardContent className="relative grid gap-4 p-6 sm:grid-cols-[1fr_auto] sm:items-end">
                <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
                    <p className="text-5xl font-semibold tracking-tight text-foreground tabular-nums sm:text-6xl">
                        {value}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        {trend ? (<span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm font-medium', trend.tone === 'up' &&
                'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', trend.tone === 'down' &&
                'border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400', trend.tone === 'flat' && 'border-border bg-muted text-muted-foreground')}>
                                {trend.tone === 'up' ? (<ArrowUpRight className="h-3.5 w-3.5" aria-hidden/>) : trend.tone === 'down' ? (<ArrowDownRight className="h-3.5 w-3.5" aria-hidden/>) : null}
                                {trend.text}
                            </span>) : null}
                        {deltaLabel ? <span className="text-sm text-muted-foreground">{deltaLabel}</span> : null}
                        {sublabel ? <span className="text-sm text-muted-foreground">{sublabel}</span> : null}
                    </div>
                </div>

                <div className="flex flex-col items-end gap-3 sm:min-w-[12rem]">
                    {action ? <div className="self-end">{action}</div> : null}
                    {sparkData.length >= 2 ? (<div className="h-14 w-full sm:w-48">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={sparkData} margin={{ left: 0, right: 0, top: 4, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="hero-kpi-spark" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={sparkColor.stroke} stopOpacity={0.35}/>
                                            <stop offset="100%" stopColor={sparkColor.stroke} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <Area type="monotone" dataKey="value" stroke={sparkColor.stroke} strokeWidth={1.75} fill="url(#hero-kpi-spark)" isAnimationActive={false}/>
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>) : null}
                </div>
            </CardContent>
        </Card>);
}
function formatTrend(value) {
    if (!Number.isFinite(value) || Math.abs(value) < 0.05) {
        return { tone: 'flat', text: '0%' };
    }
    const tone = value > 0 ? 'up' : 'down';
    const sign = value > 0 ? '+' : '';
    return { tone, text: `${sign}${value.toFixed(value % 1 === 0 ? 0 : 1)}%` };
}
/**
 * Tints the sparkline so it reinforces the delta-pill at-a-glance:
 *   - up    → emerald (matches the up pill)
 *   - down  → rose (matches the down pill)
 *   - flat / no delta → primary tint (brand-leaning but not the full
 *     saturation the v1 dashboard used).
 *
 * Returns an inline color string so it can drive both the Area stroke
 * and the gradient stops.
 */
function sparkColorForTone(tone) {
    switch (tone) {
        case 'up':
            return { stroke: 'oklch(0.72 0.17 152)' };
        case 'down':
            return { stroke: 'oklch(0.65 0.22 25)' };
        case 'flat':
        case undefined:
        default:
            return { stroke: 'var(--color-primary)' };
    }
}
